import { useCallback, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import PropTypes from 'prop-types';
import {
	BsGear,
	BsChevronDown,
	BsBuilding,
	BsPerson,
	BsBoxArrowRight,
	BsFileText,
	BsShield,
	BsWallet,
} from 'react-icons/bs';

import { useOutsideClick } from '#globalHooks/useOutsideClick';
import { BILLING_SETTINGS_PATH } from '#lib/constants/routes';
import { getUserInitials } from '#lib/textUtils';

const getCreditBarColor = (percentage) => {
	if (percentage < 25) return 'bg-warning-500';
	if (percentage < 50) return 'bg-caution-500';
	return 'bg-success-500';
};

const formatCredits = (value) => value.toLocaleString('de-DE');

const CreditRow = ({ icon: Icon, title, currentCredits, maxCredits }) => {
	const isEmpty = currentCredits <= 0;
	const hasBudget = maxCredits > 0;
	// Without a positive budget there is no ratio to show, so the bar only signals "credits left" vs. "empty".
	const percentage = hasBudget ? Math.min((currentCredits / maxCredits) * 100, 100) : isEmpty ? 0 : 100;
	const budgetSuffix = maxCredits === null ? '' : ` von ${formatCredits(maxCredits)}`;
	const label = `${title}: ${formatCredits(currentCredits)}${budgetSuffix}`;

	return (
		<div className="flex items-center gap-2 text-bgBase-600" title={label}>
			<Icon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
			<div
				className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/10"
				role="progressbar"
				aria-label={label}
				aria-valuemin={0}
				aria-valuemax={hasBudget ? maxCredits : 100}
				aria-valuenow={hasBudget ? currentCredits : percentage}
			>
				<div
					className={`h-full rounded-full transition-all ${getCreditBarColor(percentage)}`}
					style={{ width: `${percentage}%` }}
				/>
			</div>
			<span className={`text-xs min-w-[2rem] text-right tabular-nums ${isEmpty ? 'text-warning-500' : ''}`}>
				{formatCredits(currentCredits)}
			</span>
		</div>
	);
};

CreditRow.propTypes = {
	icon: PropTypes.elementType.isRequired,
	title: PropTypes.string.isRequired,
	currentCredits: PropTypes.number.isRequired,
	maxCredits: PropTypes.number,
};

const MENU_ITEM_CLASS = 'flex items-center px-3 py-2 text-sm hover:bg-bgBase-300 transition-colors';

const DropdownMenuItem = ({ icon: Icon, children, danger, href, to, ...props }) => {
	const Tag = href ? 'a' : to ? Link : 'button';
	const target = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : to ? { to } : { type: 'button' };

	return (
		<Tag className={`${MENU_ITEM_CLASS} ${danger ? 'text-red-600 hover:bg-red-50' : ''}`} {...target} {...props}>
			<Icon className="h-4 w-4 mr-2" />
			{children}
		</Tag>
	);
};

DropdownMenuItem.propTypes = {
	icon: PropTypes.elementType.isRequired,
	children: PropTypes.node.isRequired,
	danger: PropTypes.bool,
	href: PropTypes.string,
	to: PropTypes.string,
};

const UserSection = ({ user, userSettings, wallet, isCollapsed, onSignOut }) => {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const btnRef = useRef(null);
	const menuRef = useRef(null);
	const closeDropdown = useCallback(() => setDropdownOpen(false), []);
	useOutsideClick([btnRef, menuRef], closeDropdown, dropdownOpen);

	const displayName = userSettings?.fullName || user?.email?.split('@')[0] || 'User';

	const companyCredits = wallet?.companyCredits ?? 0;
	const companyCreditsMax = wallet?.companyMonthlyBudget ?? null;
	const contactCredits = wallet?.contactCredits ?? 0;
	const contactCreditsMax = wallet?.contactMonthlyBudget ?? null;

	return (
		<div className="text-white flex-shrink-0 p-3 border-t border-gray-600 relative">
			<div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
				<div className="h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
					<span className="font-semibold text-sm">
						{getUserInitials(userSettings?.fullName, user?.email)}
					</span>
				</div>

				{!isCollapsed && (
					<div className="flex-1 min-w-0">
						<p className="text-sm font-semibold text-white truncate">{displayName}</p>

						<div className="flex flex-col gap-2 mt-2">
							<CreditRow
								icon={BsBuilding}
								title="Unternehmens-Credits"
								currentCredits={companyCredits}
								maxCredits={companyCreditsMax}
							/>
							<CreditRow
								icon={BsPerson}
								title="Kontakt-Credits"
								currentCredits={contactCredits}
								maxCredits={contactCreditsMax}
							/>
						</div>
					</div>
				)}

				{!isCollapsed && (
					<button
						ref={btnRef}
						onClick={() => setDropdownOpen((open) => !open)}
						className="p-1 hover:bg-gray-700 rounded-md transition-colors"
						type="button"
					>
						<BsChevronDown className="h-4 w-4 text-bgBase-600" />
					</button>
				)}
			</div>

			{dropdownOpen && (
				<div
					ref={menuRef}
					className="absolute bottom-full left-3 right-3 mb-2 bg-white rounded-card shadow-elevated border border-bgBase-500 py-1 z-50 text-text"
				>
					<div className="px-3 py-2 text-sm font-semibold border-b border-bgBase-400">Mein Account</div>

					<DropdownMenuItem icon={BsWallet} to={BILLING_SETTINGS_PATH} onClick={closeDropdown}>
						Abo-Verwaltung
					</DropdownMenuItem>
					<DropdownMenuItem icon={BsGear} to="/settings" onClick={closeDropdown}>
						Einstellungen
					</DropdownMenuItem>

					<div className="border-t border-bgBase-400 my-1" />

					<DropdownMenuItem
						icon={BsFileText}
						href="https://www.b2b-hero.com/impressum"
						onClick={closeDropdown}
					>
						Impressum
					</DropdownMenuItem>

					<DropdownMenuItem
						icon={BsShield}
						href="https://www.b2b-hero.com/datenschutz"
						onClick={closeDropdown}
					>
						Datenschutz
					</DropdownMenuItem>

					<div className="border-t border-bgBase-400 my-1" />

					<DropdownMenuItem
						icon={BsBoxArrowRight}
						danger
						onClick={() => {
							closeDropdown();
							onSignOut();
						}}
					>
						Abmelden
					</DropdownMenuItem>
				</div>
			)}
		</div>
	);
};

UserSection.propTypes = {
	user: PropTypes.object,
	userSettings: PropTypes.shape({
		fullName: PropTypes.string,
	}),
	wallet: PropTypes.shape({
		companyCredits: PropTypes.number,
		companyMonthlyBudget: PropTypes.number,
		contactCredits: PropTypes.number,
		contactMonthlyBudget: PropTypes.number,
	}),
	isCollapsed: PropTypes.bool.isRequired,
	onSignOut: PropTypes.func.isRequired,
};

export default UserSection;
